/**
 * "Columns" popover for the Estimated Outcome waterfall — one row per
 * available column, grouped into recovery scenarios (ROCAP / Practitioner /
 * Low / High) and valuation sources (Balance Sheet, Bank Statement, … — the
 * same columns the Asset table exposes), each with an Eye / EyeOff toggle.
 * Mirrors the Asset table's ValuationColumnsMenu because visibility is shared
 * per job, not a personal setting. Toggling a column never changes any figure.
 */

import { useState } from "react";
import { Columns3, Eye, EyeOff } from "lucide-react";
import { Badge } from "@/ui/primitives/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/ui/composed/overlays/popover";
import { cn } from "@/utils/misc";

type ColumnOption = { id: string; label: string };

export function WaterfallColumnsMenu({
  groups,
  visibleIds,
  onToggle,
  disabled,
}: {
  /** Column groups, in display order. Empty groups are skipped. */
  groups: Array<{ label: string; columns: ColumnOption[] }>;
  /** Currently visible subset (column ids). */
  visibleIds: string[];
  onToggle: (id: string) => void;
  /** Viewers can open the menu but not change the shared view. */
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const visible = new Set(visibleIds);
  const nonEmptyGroups = groups.filter((g) => g.columns.length > 0);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1.5 rounded-md border border-border/60 bg-background px-2.5 py-1 text-xs text-muted-foreground transition hover:bg-secondary hover:text-foreground"
        >
          <Columns3 className="size-3.5" />
          Columns
          {visibleIds.length > 0 && (
            <Badge variant="secondary" className="ml-0.5 text-[10px]">
              {visibleIds.length}
            </Badge>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 p-1">
        {nonEmptyGroups.map((group, gi) => (
          <div
            key={group.label}
            className={cn(gi > 0 && "mt-1 border-t border-border/60 pt-1")}
          >
            <p className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
              {group.label}
            </p>
            {group.columns.map((col) => {
              const isVisible = visible.has(col.id);
              // Keep at least one column on screen — an empty waterfall is
              // meaningless. The sole remaining column can't be hidden.
              const isLastVisible = isVisible && visibleIds.length === 1;
              const itemDisabled = disabled || isLastVisible;
              return (
                <button
                  key={col.id}
                  type="button"
                  disabled={itemDisabled}
                  onClick={() => onToggle(col.id)}
                  title={
                    disabled
                      ? "Viewers can't change the shared column view"
                      : isLastVisible
                        ? "At least one column must stay visible"
                        : isVisible
                          ? "Hide column"
                          : "Show column"
                  }
                  className={cn(
                    "flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-sm transition hover:bg-secondary",
                    itemDisabled && "cursor-not-allowed opacity-60",
                  )}
                >
                  <span className="truncate text-primary/80">{col.label}</span>
                  {isVisible ? (
                    <Eye className="size-4 shrink-0 text-primary" />
                  ) : (
                    <EyeOff className="size-4 shrink-0 text-muted-foreground/50" />
                  )}
                </button>
              );
            })}
          </div>
        ))}
      </PopoverContent>
    </Popover>
  );
}
